import { useEffect } from "react";
import { LoginStatus, logout } from "../../Redux/State/Auth";
import { useAppSelector, useAppDispatch } from "../../Redux/store";
import { JWT } from "../../Utils/JWT";
import { useTranslation } from "react-i18next";
import { useGetUserQuery } from "../../Redux/Api/Users";
import LoadingPage from "../Pages/LoadingPage";
import { useNavigate } from "react-router-dom";

export interface PrivateRouteProps {
    children: React.ReactElement | Array<React.ReactElement>
}

const PrivateRoute = ({ children }: PrivateRouteProps) => {
    const loginStatus = useAppSelector(state => state.auth.status);
    const dispatch = useAppDispatch();
    const exp = JWT.read<number>('exp'); // Expiration time
    const userId = JWT.read<string>('sub'); // User ID
    const roles = JWT.read<Array<string>>('roles') || [];
    const first_name = JWT.read<string>('given_name');
    const last_name = JWT.read<string>('family_name');

    useEffect(() => {
        // If the user is not logged in, do logout
        if (loginStatus === LoginStatus.NOT_LOGGED_IN) {
            runLogout();
        }
    }, [loginStatus]);

    useEffect(() => {
        // If no JWT is present, do logout
        if (!localStorage.getItem('token')) {
            runLogout();
        }
    }, []);

    const { data, isError, isLoading } = useGetUserQuery(userId as string, {
        skip: !userId,
    });

    const { i18n } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        // Handle users that don't have access to the API call
        if (isError && roles[0] !== 'ROLE_SOCIAL') {
            runLogout();
        }
    }, [isError]);

    useEffect(() => {
        if (!data) return;
        // If data differs from the JWT, force the user to re login
        if (data.firstname !== first_name || data.lastname !== last_name || data.role !== roles[0]) {
            runLogout();
        }
    }, [data]);

    const runLogout = () => {
        dispatch(logout());
        navigate(`/${i18n.language}/login#re-login`);
    };

    // Handle JWT expiration and login status
    if (!exp || Date.now() >= exp * 1000 || loginStatus === LoginStatus.NOT_LOGGED_IN) {
        runLogout();
        return null; // Return null after logout is initiated
    }

    // If the user check isn't complete show the LoadingPage
    if (isLoading || (!data && !isError && userId)) {
        return <LoadingPage />;
    }

    return <>{children}</>;
}

export default PrivateRoute;
